import type { ApprovalChain } from '@felix/domain';
import { draftToChain, sameChain } from './approval-chain.js';
import type { FormFile, RevisionFormMode, RevisionFormValues } from './RevisionDrawer.js';
import type {
  DocumentPatch,
  OfficeDocumentActions,
  PickedFile,
  ShipLibraryActions,
  ShipStoredFile,
  UploadProgress,
} from './source.js';
import type { LibraryDetail, LibraryRevision } from './types.js';

/**
 * Yeni doküman ve Edit (yeni revizyon) kaydı (plan M8a Task 17; ADR 0035 #3): adımlar ayrı
 * istekler olduğundan biri kesilirse kayıt yeniden denenir. Tamamlanan adım tekrarlanmaz
 * (çift doküman ya da revizyon olmaz); ama kişinin hata sonrası değiştirdiği alan da yok
 * sayılmaz (inceleme WF-3, T3):
 *  - açılmış dokümanın değişen alanları (no, başlık, tür, klasör, açıklama, kapsam) `PATCH` ile
 *    güncellenir;
 *  - revizyon açıldıktan sonra revizyon alanları ve ana dosya kilitlenir (`lockedFields`);
 *    form onları değiştirtmez.
 * Edit'te revizyon önce açılır, kapsam değişikliği sonra yazılır: revizyon açılamazsa (ör.
 * `OPEN_REVISION_EXISTS`) yürürlükteki revizyonun dağıtımı değişmez (inceleme WF-2).
 *
 * Aynı adımlar gemide geminin kendi belgesi için de geçerlidir (M8a4e; ADR 0038 §10, §13): dosyayı
 * ana süreç gemi sunucusuna akıtır, revizyon ucu yükleme yanıtındaki dosya bilgisini alır. Gemi
 * belgesinde kapsam yoktur (örtük, ADR 0038 §12).
 */

type DocumentValues = Pick<
  RevisionFormValues,
  | 'folderId'
  | 'docNo'
  | 'title'
  | 'kind'
  | 'description'
  | 'scope'
  | 'scopeVesselIds'
  | 'approvalChain'
>;

export interface SaveState {
  /** Yeni dokümanda açılan doküman ve yazıldığı değerler. */
  document?: { id: string; rowVersion: number; values: NormalizedDocument };
  /** Edit'te yazılan kapsam ve dokümanın son satır sürümü. */
  scope?: { scope: string; vesselIds: string[]; rowVersion: number };
  /** Edit'te yazılan onay zinciri ve dokümanın son satır sürümü (ADR 0071 §2). */
  chain?: { value: ApprovalChain | null; rowVersion: number };
  /**
   * Dosya anahtarı → yükleme sonucu: ofiste tus yükleme kimliği (kesilen yükleme kaldığı yerden),
   * gemide gemi sunucusunun dosya bilgisi.
   */
  uploads: Map<string, unknown>;
  revision?: LibraryRevision;
  attached: Set<string>;
  /** Revizyona yazılmış baskı PDF'inin dosya anahtarı (ADR 0055 §5). */
  printCopy?: string;
}

export function createSaveState(): SaveState {
  return { uploads: new Map(), attached: new Set() };
}

/** Revizyon açıldıktan sonra değiştirilemeyen alanlar (form bunları kilitler). */
export function lockedFields(state: SaveState): (keyof RevisionFormValues)[] {
  if (!state.revision) return [];
  return state.printCopy
    ? ['revisionNo', 'changeSummary', 'file', 'printCopy']
    : ['revisionNo', 'changeSummary', 'file'];
}

/**
 * Revizyona yazılmış eklerin dosya anahtarları (inceleme M8a3-R9): ek silme ucu yok (fiziksel
 * silme yok), formdan kaldırılırsa revizyonda kalırdı; form onları kilitler.
 */
export function lockedAttachments(state: SaveState): string[] {
  return [...state.attached];
}

/**
 * Edit'te formun kapsamı dokümanın şimdiki kapsamından farklı mı (inceleme M8a3-R2). Kapsam
 * dokümanındır: değişiklik yeni revizyonun yayınını beklemez, yürürlükteki revizyona hemen
 * uygulanır (ADR 0028 #28).
 */
export function scopeChanged(
  current: { scope: string; scopeVesselIds: readonly string[] },
  next: { scope: string; scopeVesselIds: readonly string[] },
): boolean {
  const vessels = next.scope === 'vessels' ? next.scopeVesselIds : [];
  const saved = current.scope === 'vessels' ? current.scopeVesselIds : [];
  return current.scope !== next.scope || !sameList(saved, vessels);
}

export const fileKey = (file: Pick<FormFile, 'name' | 'size' | 'lastModified'>) =>
  `${file.name}:${file.size}:${file.lastModified}`;

interface NormalizedDocument {
  folderId: string;
  docNo: string;
  title: string;
  kind: RevisionFormValues['kind'];
  description: string | null;
  scope: RevisionFormValues['scope'];
  scopeVesselIds: string[];
  /** Formun onay zinciri (ADR 0071 §2); form değilse ya da hazır kuralda `null`. */
  approvalChain: ApprovalChain | null;
}

/** Formun zinciri: yalnız `form` türünde; taslak verilmemişse hazır kural (`null`). */
function chainOf(values: Pick<DocumentValues, 'kind' | 'approvalChain'>): ApprovalChain | null {
  return values.kind === 'form' && values.approvalChain ? draftToChain(values.approvalChain) : null;
}

function normalize(values: DocumentValues): NormalizedDocument {
  const description = values.description.trim();
  return {
    folderId: values.folderId,
    docNo: values.docNo.trim(),
    title: values.title.trim(),
    kind: values.kind,
    description: description === '' ? null : description,
    scope: values.scope,
    scopeVesselIds: values.scope === 'vessels' ? [...values.scopeVesselIds] : [],
    approvalChain: chainOf(values),
  };
}

const sameList = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

/** Açılmış dokümanla formun farkı (`PATCH` gövdesi); fark yoksa boş. */
function documentChanges(saved: NormalizedDocument, next: NormalizedDocument, scoped: boolean) {
  const patch: Record<string, unknown> = {};
  for (const key of ['folderId', 'docNo', 'title', 'kind', 'description'] as const) {
    if (saved[key] !== next[key]) patch[key] = next[key];
  }
  if (
    scoped &&
    (saved.scope !== next.scope || !sameList(saved.scopeVesselIds, next.scopeVesselIds))
  ) {
    patch.scope = next.scope;
    patch.scopeVesselIds = next.scopeVesselIds;
  }
  if (scoped && !sameChain(saved.approvalChain, next.approvalChain)) {
    patch.approvalChain = next.approvalChain;
  }
  return patch;
}

/** Kaydın uçları: ofiste şirket kütüphanesi, gemide geminin kendi kütüphanesi. */
interface DraftWriter {
  upload(
    file: FormFile,
    options: { onProgress: (progress: UploadProgress) => void; signal?: AbortSignal },
  ): Promise<unknown>;
  createDocument(values: NormalizedDocument): Promise<{ id: string; rowVersion: number }>;
  updateDocument(
    id: string,
    rowVersion: number,
    patch: Record<string, unknown>,
  ): Promise<{ id: string; rowVersion: number }>;
  createRevision(
    documentId: string,
    input: { upload: unknown; fileName: string; revisionNo: string; changeSummary: string },
  ): Promise<LibraryRevision>;
  addAttachment(
    revisionId: string,
    input: { upload: unknown; fileName: string; role?: 'print_rendition' },
  ): Promise<void>;
  /** Kapsam dokümanın alanı mı (ofis); gemi belgesinde kapsam yok. */
  scoped: boolean;
}

function officeWriter(office: OfficeDocumentActions): DraftWriter {
  return {
    upload: (file, options) => office.upload(file as File, options),
    createDocument: (values) => office.createDocument(values),
    updateDocument: (id, rowVersion, patch) =>
      office.updateDocument(id, rowVersion, patch as DocumentPatch),
    createRevision: (documentId, input) =>
      office.createRevision(documentId, {
        uploadId: String(input.upload),
        fileName: input.fileName,
        revisionNo: input.revisionNo,
        changeSummary: input.changeSummary,
      }),
    addAttachment: (revisionId, input) =>
      office.addAttachment(revisionId, {
        uploadId: String(input.upload),
        fileName: input.fileName,
        ...(input.role ? { role: input.role } : {}),
      }),
    scoped: true,
  };
}

const VESSEL_DOCUMENT_FIELDS = ['folderId', 'docNo', 'title', 'kind', 'description'] as const;

function shipWriter(ship: ShipLibraryActions): DraftWriter {
  const fields = (values: Record<string, unknown>) =>
    Object.fromEntries(
      VESSEL_DOCUMENT_FIELDS.filter((key) => key in values).map((key) => [key, values[key]]),
    );
  return {
    upload: (file, options) => ship.upload(file as PickedFile, options),
    createDocument: (values) =>
      ship.createDocument({
        folderId: values.folderId,
        docNo: values.docNo,
        title: values.title,
        kind: values.kind,
        description: values.description,
      }),
    updateDocument: (id, rowVersion, patch) => ship.updateDocument(id, rowVersion, fields(patch)),
    createRevision: (documentId, input) =>
      ship.createRevision(documentId, {
        file: input.upload as ShipStoredFile,
        revisionNo: input.revisionNo,
        changeSummary: input.changeSummary,
      }),
    addAttachment: (revisionId, input) =>
      ship.addAttachment(revisionId, {
        file: input.upload as ShipStoredFile,
        ...(input.role ? { role: input.role } : {}),
      }),
    scoped: false,
  };
}

type ProgressListener = (progress: { label: string } & UploadProgress) => void;

export function saveRevisionDraft(
  office: OfficeDocumentActions,
  mode: RevisionFormMode,
  detail: LibraryDetail | null,
  values: RevisionFormValues,
  state: SaveState,
  onProgress: ProgressListener = () => undefined,
  /** Çekmece kapanınca kesilir: yükleme durur, hiçbir adım yazılmaz (inceleme M8a3-R11). */
  signal?: AbortSignal,
): Promise<{ documentId: string; revision: LibraryRevision }> {
  return saveDraft(officeWriter(office), mode, detail, values, state, onProgress, signal);
}

/** Gemide geminin kendi belgesi: yeni belge ya da yeni revizyon (ADR 0038 §10, §13). */
export function saveShipRevisionDraft(
  ship: ShipLibraryActions,
  mode: RevisionFormMode,
  detail: LibraryDetail | null,
  values: RevisionFormValues,
  state: SaveState,
  onProgress: ProgressListener = () => undefined,
  signal?: AbortSignal,
): Promise<{ documentId: string; revision: LibraryRevision }> {
  return saveDraft(shipWriter(ship), mode, detail, values, state, onProgress, signal);
}

async function saveDraft(
  writer: DraftWriter,
  mode: RevisionFormMode,
  detail: LibraryDetail | null,
  values: RevisionFormValues,
  state: SaveState,
  onProgress: ProgressListener,
  signal?: AbortSignal,
): Promise<{ documentId: string; revision: LibraryRevision }> {
  if (!values.file) throw Object.assign(new Error('FILE_REQUIRED'), { code: 'FILE_REQUIRED' });
  // Edit'te kapsam değişikliği açık onay ister; onaysız hiçbir adım yazılmaz (inceleme M8a3-R2)
  if (writer.scoped && mode === 'revision' && detail && !values.scopeConfirmed) {
    const saved = state.scope
      ? { scope: state.scope.scope, scopeVesselIds: state.scope.vesselIds }
      : detail;
    if (scopeChanged(saved, values)) throw failure('SCOPE_CHANGE_UNCONFIRMED');
  }
  // Revizyona yazılmış ek kaldırılamaz: sunucuda ek silme yok, revizyonda kalırdı (M8a3-R9)
  const keys = new Set(values.attachments.map(fileKey));
  if ([...state.attached].some((key) => !keys.has(key))) throw failure('ATTACHMENT_LOCKED');
  // Yazılmış baskı PDF'i de kilitlidir (ADR 0055 §5)
  const printCopy = values.printCopy ?? null;
  if (state.printCopy && (!printCopy || fileKey(printCopy) !== state.printCopy)) {
    throw failure('ATTACHMENT_LOCKED');
  }
  const pendingPrint = printCopy && !state.printCopy ? printCopy : null;

  const upload = async (file: FormFile) => {
    const key = fileKey(file);
    if (state.uploads.has(key)) return state.uploads.get(key);
    onProgress({ label: file.name, sent: 0, total: file.size });
    const id = await writer.upload(file, {
      onProgress: ({ sent, total }) => onProgress({ label: file.name, sent, total }),
      ...(signal ? { signal } : {}),
    });
    state.uploads.set(key, id);
    return id;
  };

  // Dosyalar önce yüklenir; doküman ve revizyon ancak yükleme bitince yazılır: kesilen yükleme
  // revizyonsuz doküman bırakmaz (inceleme M8a3-R10)
  const file = values.file;
  const pending = values.attachments.filter((a) => !state.attached.has(fileKey(a)));
  const mainUpload = state.revision ? null : await upload(file);
  for (const attachment of pending) await upload(attachment);
  if (pendingPrint) await upload(pendingPrint);
  // Kapanınca bekleyen adım çalışmaz; yazmalar başladıktan sonra yarıda bırakılmaz
  if (signal?.aborted) throw failure('CANCELLED');

  let documentId = detail?.id ?? '';
  if (mode === 'new') {
    const next = normalize(values);
    if (!state.document) {
      const created = await writer.createDocument(next);
      state.document = { id: created.id, rowVersion: created.rowVersion, values: next };
    } else {
      const patch = documentChanges(state.document.values, next, writer.scoped);
      if (Object.keys(patch).length > 0) {
        const updated = await writer.updateDocument(
          state.document.id,
          state.document.rowVersion,
          patch,
        );
        state.document = { id: updated.id, rowVersion: updated.rowVersion, values: next };
      }
    }
    documentId = state.document.id;
  }

  // Revizyon açıldıysa ana dosya ve revizyon alanları kilitli: açılan revizyon geçerlidir
  if (!state.revision && mainUpload !== null) {
    state.revision = await writer.createRevision(documentId, {
      upload: mainUpload,
      fileName: file.name,
      revisionNo: values.revisionNo.trim(),
      changeSummary: values.changeSummary.trim(),
    });
  }
  const revision = state.revision;
  if (!revision) throw failure('GENERIC');
  for (const attachment of pending) {
    const id = await upload(attachment);
    await writer.addAttachment(revision.id, { upload: id, fileName: attachment.name });
    state.attached.add(fileKey(attachment));
  }
  // Baskı PDF'i: Word ya da Excel ana dosyanın aslına uygun basılan kopyası (ADR 0028 #34(1))
  if (pendingPrint) {
    const id = await upload(pendingPrint);
    await writer.addAttachment(revision.id, {
      upload: id,
      fileName: pendingPrint.name,
      role: 'print_rendition',
    });
    state.printCopy = fileKey(pendingPrint);
  }

  // Edit: kapsam değişikliği revizyon açıldıktan sonra (inceleme WF-2); gemi belgesinde yok
  if (writer.scoped && mode === 'revision' && detail) {
    const vesselIds = values.scope === 'vessels' ? [...values.scopeVesselIds] : [];
    const saved = state.scope ?? {
      scope: detail.scope,
      vesselIds: detail.scopeVesselIds,
      rowVersion: state.chain?.rowVersion ?? detail.rowVersion,
    };
    if (saved.scope !== values.scope || !sameList(saved.vesselIds, vesselIds)) {
      const updated = await writer.updateDocument(detail.id, saved.rowVersion, {
        scope: values.scope,
        scopeVesselIds: vesselIds,
      });
      state.scope = { scope: values.scope, vesselIds, rowVersion: updated.rowVersion };
    }
    // Onay zinciri dokümanındır; yalnız sonraki gönderimlere işler (ADR 0071 §2, §3)
    const chain = chainOf(values);
    const current = state.chain?.value ?? detail.approvalChain ?? null;
    if (!sameChain(current, chain)) {
      const rowVersion = state.chain?.rowVersion ?? state.scope?.rowVersion ?? detail.rowVersion;
      const updated = await writer.updateDocument(detail.id, rowVersion, { approvalChain: chain });
      state.chain = { value: chain, rowVersion: updated.rowVersion };
    }
  }
  return { documentId, revision };
}

const failure = (code: string) => Object.assign(new Error(code), { code });
